import { Box, Typography } from "@mui/material";
import { memo } from "react";
import { QUIZ } from "@/theme/quizTokens";

type MealMode = "FULL" | "MIXED";

type Props = {
    mealMode: MealMode;
    onToggle: (mode: MealMode) => void;
    dogName?: string;
};

const OPTIONS: { mode: MealMode; label: string }[] = [
    { mode: "FULL", label: "100% Paws" },
    { mode: "MIXED", label: "60% Paws" },
];

/** Segmented toggle (blue active) for FULL vs MIXED meal mode, sits inside the brown plan card. */
function MealModeToggle({ mealMode, onToggle, dogName }: Props) {
    const namePossessive = dogName?.trim() ? `${dogName.trim()}'s` : "your dog's";

    return (
        <Box
            sx={{
                mb: { xs: "16px", md: "24px" } }}>
        <Box
            sx={{
                width: "100%",
                background: QUIZ.color.white,
                borderRadius: QUIZ.radius.pill,
                p: "5px",
                display: "flex",
                gap: "4px",
            }}
        >
            {OPTIONS.map((opt) => {
                const active = mealMode === opt.mode;
                return (
                    <Box
                        key={opt.mode}
                        role="button"
                        onClick={() => onToggle(opt.mode)}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggle(opt.mode);
                        }}
                        sx={{
                            flex: 1,
                            textAlign: "center",
                            fontFamily: QUIZ.font.body,
                            fontWeight: 700,
                            fontSize: { xs: 14, md: 19 },
                            borderRadius: QUIZ.radius.pill,
                            py: { xs: "8px", md: "14px" },
                            px: "8px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            userSelect: "none",
                            touchAction: "manipulation",
                            background: active ? QUIZ.color.blue : "transparent",
                            color: active ? QUIZ.color.dark : QUIZ.color.grey,
                            transition: "background 0.2s ease, color 0.2s ease",
                        }}
                    >
                        {opt.label}
                    </Box>
                );
            })}
            </Box>

            {/* Explainer line beneath the toggle */}
            <Typography
                sx={{
                    fontFamily: QUIZ.font.body,
                    fontSize: { xs: 12, md: 14 },
                    color: QUIZ.color.cream,
                    opacity: 0.85,
                    textAlign: "center",
                    mt: { xs: "10px", md: "14px" },
                    px: { xs: "8px", md: "0px" },
                }}
            >
                {mealMode === "FULL"
                    ? `Every meal, fully balanced. Switch to 60% to feed Paws alongside ${namePossessive} current food.`
                    : `60% Paws, 40% ${namePossessive} current food — a gentle way to introduce fresh meals.`}
            </Typography>
        </Box>
    );
}

export default memo(MealModeToggle);
